import envService from "@/env.service";
import {
  type Auth0Client,
  type Auth0ClientOptions,
  type CacheLocation,
  createAuth0Client,
} from "@auth0/auth0-spa-js";
import { datadogRum } from "@datadog/browser-rum";

const cacheLocation: CacheLocation = "localstorage";

const auth0Config = {
  domain: envService.env.auth0.domain,
  clientId: envService.env.auth0.clientId,
  authorizationParams: {
    audience: envService.env.auth0.audience,
    redirect_uri: envService.env.auth0.callbackUrl,
    scope: "openid profile email offline_access",
  },
  cacheLocation,
  useRefreshTokens: true,
};

class AuthClient {
  private _auth0Config: Auth0ClientOptions;
  private _instance: Auth0Client | null = null;

  constructor(auth0Config: Auth0ClientOptions) {
    this._auth0Config = auth0Config;
  }

  public async getInstance(): Promise<Auth0Client> {
    if (this._instance) return this._instance;
    this._instance = await createAuth0Client(this._auth0Config);
    return this._instance;
  }

  public async isAuthenticated() {
    const instance = await this.getInstance();
    return instance.isAuthenticated();
  }

  public async getUser() {
    const instance = await this.getInstance();
    return await instance.getUser();
  }

  public async handleRedirectCallback() {
    const instance = await this.getInstance();
    const result = await instance.handleRedirectCallback();
    return result;
  }

  public async loginWithRedirect(params?: {
    returnTo?: string;
    organization?: string | null;
    invitation?: string | null;
  }) {
    const instance = await this.getInstance();

    // Build authorizationParams only with defined values
    const authorizationParams: Record<string, string> = {};
    if (params?.invitation) authorizationParams.invitation = params.invitation;
    if (params?.organization)
      authorizationParams.organization = params.organization;

    await instance.loginWithRedirect({
      ...(Object.keys(authorizationParams).length > 0 && {
        authorizationParams,
      }),
      ...(params?.returnTo && { appState: { returnTo: params.returnTo } }),
    });
  }

  public async logout(options?: {
    logoutParams?: {
      returnTo: string;
    };
  }) {
    const instance = await this.getInstance();
    await instance.logout(options);
    datadogRum.clearUser();
  }

  public async getTokenSilently() {
    const instance = await this.getInstance();
    return await instance.getTokenSilently({
      authorizationParams: {
        audience: envService.env.auth0.audience,
      },
    });
  }
}

export default new AuthClient(auth0Config);
